import React, { useState } from 'react';
import { Card, Table, Tag, Button, Space, Empty, Badge, Statistic, Row, Col, Modal, message, Tabs, Tooltip } from 'antd';
import { BellOutlined, DeleteOutlined, CheckOutlined, WarningOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const PaymentNotifications = () => {
    const { t, i18n } = useTranslation();
    const queryClient = useQueryClient();
    const [selectedNotifications, setSelectedNotifications] = useState([]);

    // Fetch all notifications
    const { data: notificationsData, isLoading, refetch } = useQuery('payment-notifications', async () => {
        const response = await api.get('/payment-notifications');
        return response.data.data || [];
    });

    // Fetch notification counts
    const { data: countsData } = useQuery('notification-counts', async () => {
        const response = await api.get('/payment-notifications/count/by-status');
        return response.data.data || { active: 0, overdue: 0, unread: 0 };
    });

    // Fetch overdue notifications
    const { data: overdueData } = useQuery('overdue-notifications', async () => {
        const response = await api.get('/payment-notifications/overdue');
        return response.data.data || [];
    });

    // Fetch urgent notifications
    const { data: urgentData } = useQuery('urgent-notifications', async () => {
        const response = await api.get('/payment-notifications/urgent');
        return response.data.data || [];
    });

    // Mark as read mutation
    const markAsReadMutation = useMutation(
        (notificationId) => api.put(`/payment-notifications/${notificationId}/read`),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('payment-notifications');
                queryClient.invalidateQueries('notification-counts');
            },
            onError: () => {
                message.error(t('failedToUpdateNotification') || 'Failed to update notification');
            }
        }
    );

    // Dismiss notification mutation
    const dismissMutation = useMutation(
        (notificationId) => api.put(`/payment-notifications/${notificationId}/dismiss`),
        {
            onSuccess: () => {
                message.success(t('notificationDismissed') || 'Notification dismissed');
                queryClient.invalidateQueries('payment-notifications');
                queryClient.invalidateQueries('notification-counts');
            },
            onError: () => {
                message.error(t('failedToDismissNotification') || 'Failed to dismiss notification');
            }
        }
    );

    // Complete notification mutation
    const completeMutation = useMutation(
        (notificationId) => api.put(`/payment-notifications/${notificationId}/complete`),
        {
            onSuccess: () => {
                message.success(t('paymentNotificationCompleted') || 'Notification marked as completed');
                queryClient.invalidateQueries('payment-notifications');
                queryClient.invalidateQueries('notification-counts');
            },
            onError: () => {
                message.error(t('failedToCompleteNotification') || 'Failed to complete notification');
            }
        }
    );

    // Mark all as read mutation
    const markAllAsReadMutation = useMutation(
        () => api.put('/payment-notifications/read/all'),
        {
            onSuccess: () => {
                message.success(t('allNotificationsMarkedAsRead') || 'All notifications marked as read');
                queryClient.invalidateQueries('payment-notifications');
                queryClient.invalidateQueries('notification-counts');
            },
            onError: () => {
                message.error(t('failedToMarkAsRead') || 'Failed to mark as read');
            }
        }
    );

    const formatCurrency = (amount, currency = 'USD') => {
        const symbol = currency === 'IQD' ? 'IQD ' : '$ ';
        return `${symbol}${amount?.toLocaleString() || '0'}`;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'payment-overdue':
                return 'red';
            case 'payment-due':
                return 'orange';
            case 'payment-completed':
                return 'green';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case 'payment-overdue':
                return t('overdue') || 'Overdue';
            case 'payment-due':
                return t('dueForPayment') || 'Due for Payment';
            case 'payment-completed':
                return t('paymentCompleted') || 'Completed';
            default:
                return status;
        }
    };

    const getDaysLabel = (days) => {
        if (days < 0) {
            return <span style={{ color: '#dc2626', fontWeight: 600 }}>{Math.abs(days)} {t('daysOverdue') || 'days overdue'}</span>;
        } else if (days === 0) {
            return <span style={{ color: '#dc2626', fontWeight: 600 }}>{t('today') || 'Today'}</span>;
        } else if (days <= 3) {
            return <span style={{ color: '#ea580c', fontWeight: 600 }}>{days} {t('daysLeft') || 'days left'}</span>;
        } else {
            return <span style={{ color: '#16a34a', fontWeight: 600 }}>{days} {t('daysLeft') || 'days left'}</span>;
        }
    };

    const columns = [
        {
            title: t('student') || 'Student',
            key: 'student',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>
                        {record.student?.firstName} {record.student?.lastName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        ID: {record.student?.studentId}
                    </div>
                </div>
            ),
            width: 180
        },
        {
            title: t('course') || 'Course',
            key: 'course',
            render: (_, record) => (
                <div>
                    <div style={{ fontWeight: 600 }}>{record.course?.name}</div>
                    <div style={{ fontSize: '12px', color: '#666' }}>{record.course?.code}</div>
                </div>
            ),
            width: 150
        },
        {
            title: t('remainingAmount') || 'Remaining',
            key: 'remaining',
            render: (_, record) => (
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#dc2626' }}>
                    {formatCurrency(record.remainingAmount, record.currency)}
                </span>
            ),
            width: 120
        },
        {
            title: t('paymentDeadline') || 'Deadline',
            key: 'deadline',
            render: (_, record) => {
                const deadline = new Date(record.paymentDeadline);
                return (
                    <div>
                        <div style={{ fontSize: '12px', fontWeight: 500 }}>
                            {deadline.toLocaleDateString()}
                        </div>
                        <div style={{ marginTop: '4px' }}>
                            {getDaysLabel(record.daysUntilDeadline)}
                        </div>
                    </div>
                );
            },
            width: 140
        },
        {
            title: t('status') || 'Status',
            key: 'type',
            render: (_, record) => (
                <Tag color={getStatusColor(record.type)}>
                    {record.type === 'payment-overdue' && <WarningOutlined />}
                    {record.type === 'payment-due' && <ClockCircleOutlined />}
                    {' '}
                    {getStatusLabel(record.type)}
                </Tag>
            ),
            width: 130
        },
        {
            title: t('actions') || 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space size="small">
                    {!record.isRead && (
                        <Tooltip title={t('markAsRead') || 'Mark as read'}>
                            <Button
                                type="text"
                                icon={<CheckOutlined />}
                                onClick={() => markAsReadMutation.mutate(record._id)}
                                size="small"
                            />
                        </Tooltip>
                    )}
                    <Tooltip title={t('dismiss') || 'Dismiss'}>
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            onClick={() => dismissMutation.mutate(record._id)}
                            size="small"
                        />
                    </Tooltip>
                </Space>
            ),
            width: 100
        }
    ];

    const notificationsWithoutCompleted = notificationsData?.filter(n => n.status !== 'completed') || [];
    const overdue = overdueData || [];
    const urgent = urgentData || [];

    return (
        <div>
            <div style={{ marginBottom: 24 }}>
                <h1 style={{ marginBottom: 16 }}>{t('paymentNotifications') || 'Payment Notifications'}</h1>

                {/* Statistics Cards */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                        <Card bordered={false}>
                            <Statistic
                                title={t('activeNotifications') || 'Active'}
                                value={countsData?.active || 0}
                                prefix={<BellOutlined />}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card bordered={false}>
                            <Statistic
                                title={t('overduePayments') || 'Overdue'}
                                value={countsData?.overdue || 0}
                                prefix={<WarningOutlined />}
                                valueStyle={{ color: '#dc2626' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                        <Card bordered={false}>
                            <Statistic
                                title={t('unreadNotifications') || 'Unread'}
                                value={countsData?.unread || 0}
                                prefix={<Badge count={countsData?.unread || 0} />}
                                valueStyle={{ color: '#ea580c' }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>

            <Tabs
                defaultActiveKey="all"
                items={[
                    {
                        key: 'all',
                        label: `${t('allNotifications') || 'All Notifications'} (${notificationsWithoutCompleted.length})`,
                        children: (
                            <Card
                                bordered={false}
                                style={{ marginTop: 16 }}
                                extra={
                                    countsData?.unread > 0 && (
                                        <Button
                                            type="primary"
                                            size="small"
                                            onClick={() => markAllAsReadMutation.mutate()}
                                            loading={markAllAsReadMutation.isLoading}
                                        >
                                            {t('markAllAsRead') || 'Mark All as Read'}
                                        </Button>
                                    )
                                }
                            >
                                {notificationsWithoutCompleted.length === 0 ? (
                                    <Empty
                                        description={t('noNotifications') || 'No notifications'}
                                        style={{ marginTop: 32 }}
                                    />
                                ) : (
                                    <Table
                                        columns={columns}
                                        dataSource={notificationsWithoutCompleted}
                                        rowKey="_id"
                                        pagination={{ pageSize: 10 }}
                                        loading={isLoading}
                                        size="small"
                                    />
                                )}
                            </Card>
                        )
                    },
                    {
                        key: 'urgent',
                        label: `${t('urgentPayments') || 'Urgent Payments'} (${urgent.length})`,
                        children: (
                            <Card
                                bordered={false}
                                style={{ marginTop: 16 }}
                            >
                                {urgent.length === 0 ? (
                                    <Empty
                                        description={t('noUrgentPayments') || 'No urgent payments'}
                                        style={{ marginTop: 32 }}
                                    />
                                ) : (
                                    <Table
                                        columns={columns}
                                        dataSource={urgent}
                                        rowKey="_id"
                                        pagination={{ pageSize: 10 }}
                                        loading={isLoading}
                                        size="small"
                                        rowClassName={(record) => {
                                            if (record.type === 'payment-overdue') return 'ant-table-row-danger';
                                            if (record.daysUntilDeadline <= 3) return 'ant-table-row-warning';
                                            return '';
                                        }}
                                    />
                                )}
                            </Card>
                        )
                    },
                    {
                        key: 'overdue',
                        label: `${t('overduePayments') || 'Overdue Payments'} (${overdue.length})`,
                        children: (
                            <Card
                                bordered={false}
                                style={{ marginTop: 16 }}
                            >
                                {overdue.length === 0 ? (
                                    <Empty
                                        description={t('noOverduePayments') || 'No overdue payments'}
                                        style={{ marginTop: 32 }}
                                    />
                                ) : (
                                    <Table
                                        columns={columns}
                                        dataSource={overdue}
                                        rowKey="_id"
                                        pagination={{ pageSize: 10 }}
                                        loading={isLoading}
                                        size="small"
                                        rowClassName={() => 'ant-table-row-danger'}
                                    />
                                )}
                            </Card>
                        )
                    }
                ]}
            />

            <style>{`
                .ant-table-row-danger {
                    background-color: #fee2e2 !important;
                }
                .ant-table-row-danger:hover > td {
                    background-color: #fecaca !important;
                }
                .ant-table-row-warning {
                    background-color: #fef3c7 !important;
                }
                .ant-table-row-warning:hover > td {
                    background-color: #fde68a !important;
                }
            `}</style>
        </div>
    );
};

export default PaymentNotifications;
